import React, {CSSProperties, memo, useState} from 'react'

import {P} from '../index.js'
import {Check} from '../forms/index.js'
import isTouchDevice from '../../../utils/IsTouchDevice.js'
import {CellLayout, TableRowData} from './types.js'
import {checkboxCellStyle, checkboxGapStyle, getCellStyle, getRowBackground, getRowStyle, tableSizes} from './styles.js'
import {getValue} from './utils.js'
import t from '../../../helpers/Languages.js'

// Touch devices have no hover, showOnHover content is always shown there
const touchDevice = typeof window !== 'undefined' && isTouchDevice()

interface TableRowProps {
    row: TableRowData
    cells: CellLayout[]
    /** Stable: the Table passes the same function on every render */
    onSelect: (id: string, shiftKey?: boolean) => void
    selected: boolean
    background?: string
    canCheck?: boolean
}

// Clicking these in a cell is for them, not for (de)selecting the row or column.onClick
const interactiveSelector = [
    'a', 'button', 'input', 'select', 'textarea', 'label',
    '[role=button]', '[role=checkbox]', '[role=switch]', '[role=radio]', '[role=link]', '[role=menuitem]', '[role=option]', '[role=tab]',
    '[contenteditable=""]', '[contenteditable=true]'
].join(', ')

/**
 * Whether a click in a cell was on interactive content in it (a button, link, field), or in a portal rendered from
 * the cell (a Popover's panel), which React bubbles to the cell too.
 */
function isInteractiveClick(event: React.MouseEvent<HTMLElement>) {
    const cell = event.currentTarget
    const target = event.target

    if (!(target instanceof Element) || !cell.contains(target)) {
        return true
    }

    const interactive = target.closest(interactiveSelector)

    return !!interactive && interactive !== cell && cell.contains(interactive)
}

// Underlines a clickable cell on hover straight on the element, so hovering doesn't re-render the row
function setUnderline(event: React.MouseEvent<HTMLElement>, underline: boolean) {
    event.currentTarget.style.textDecoration = underline ? 'underline' : 'none'
}

/**
 * One row. Memoised: it only re-renders when its row, selection, background or the columns change, or when it is
 * hovered (for the hover background and showOnHover columns).
 */
const TableRow = ({row, cells, onSelect, selected, background, canCheck}: TableRowProps) => {
    const [hover, setHover] = useState(false)

    return (
        <div
            role='row'
            aria-selected={canCheck ? selected : undefined}
            style={{width: '100%', background: getRowBackground(background)}}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onMouseDown={(event) => {
                // Prevent text selection when using shift key to select multiple rows
                if (event.shiftKey) {
                    event.preventDefault()
                }
            }}
        >
            <div style={getRowStyle({hovered: hover})}>
                {canCheck && (
                    <div role='cell' style={checkboxCellStyle}>
                        <Check aria-label={t('tableSelectRow')} checked={selected} onChange={(event) => onSelect(row._id, event.shiftKey)} />
                        <div style={checkboxGapStyle} />
                    </div>
                )}

                {cells.map(({id, column, width}) => {
                    const clickable = !!column.onClick

                    const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
                        if (isInteractiveClick(event)) {
                            return
                        }

                        if (column.onClick) {
                            column.onClick(row)
                        } else if (canCheck) {
                            onSelect(row._id, event.shiftKey)
                        }
                    }

                    const cellStyle: CSSProperties = {
                        ...getCellStyle(width),
                        cursor: clickable || canCheck ? 'pointer' : undefined,
                        minHeight: tableSizes.rowHeight,
                        overflow: 'hidden'
                    }

                    return (
                        <div
                            key={id}
                            role='cell'
                            style={cellStyle}
                            onClick={onClick}
                            onMouseEnter={clickable ? (event) => setUnderline(event, true) : undefined}
                            onMouseLeave={clickable ? (event) => setUnderline(event, false) : undefined}
                        >
                            {column.showOnHover && !touchDevice && !hover ?
                                null :
                                column.render ?
                                    column.render(row, {hovered: hover || touchDevice}) :
                                    <P ellipsis>{getValue(row, column)}</P>
                            }
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default memo(TableRow)
